/**
 * The recommendations model returns JSON ({ intro, recommandations: [{ action, detail }] }, DO spec). The
 * editor stores HTML (htmlToSlate / slateToHtml), so we render the JSON into a small HTML block: an intro
 * paragraph plus a bulleted list where each item leads with the bold action, then its detail.
 *
 * The model's output is not always clean JSON: it can arrive wrapped in a code fence or a sentence, and its
 * texts carry light markdown (**bold**, "1." / "-" list markers). Both are normalised here so the editor
 * never shows raw markup, and the character counter counts what the reader actually sees (bug #15689).
 */
interface RecommendationItem {
    action?: string;
    detail?: string;
}

interface RecommendationsResult {
    intro?: string;
    recommandations?: RecommendationItem[];
}

const escapeHtml = (text: string): string =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

/** Keeps only the JSON object, dropping a code fence or any prose the model wrapped around it. */
const extractJsonObject = (content: string): string => {
    const start = content.indexOf('{');
    const end = content.lastIndexOf('}');

    return start >= 0 && end > start ? content.slice(start, end + 1) : content;
};

/** Drops a leading list marker ("1.", "1)", "-", "*", "•") the model may put in front of a line. */
const stripListMarker = (line: string): string => line.trim().replace(/^(?:[-*•]|\d+[.)])\s+/, '');

/** Escapes the text, then turns the model's **bold** markers into real markup; lines join into one flow. */
const toInlineHtml = (text: string): string =>
    escapeHtml(text)
        .split('\n')
        .map(stripListMarker)
        .filter(Boolean)
        .join(' ')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

/** Bold is already carried by the <strong> wrapper, so the action keeps no markers of its own. */
const toActionHtml = (text: string): string => toInlineHtml(text).replace(/<\/?strong>/g, '');

/** Last-resort rendering when the response is not JSON at all: readable paragraphs, never raw markup. */
const toParagraphs = (content: string): string =>
    content
        .split(/\n{2,}/)
        .map((block) => toInlineHtml(block))
        .filter(Boolean)
        .map((block) => `<p>${block}</p>`)
        .join('');

export const recommendationsJsonToHtml = (content: string): string => {
    let parsed: RecommendationsResult | null = null;
    try {
        parsed = JSON.parse(extractJsonObject(content)) as RecommendationsResult;
    } catch {
        parsed = null;
    }

    if (!parsed || typeof parsed !== 'object') {
        return toParagraphs(content.trim());
    }

    const parts: string[] = [];

    const intro = parsed.intro?.trim();
    if (intro) {
        parts.push(`<p>${toInlineHtml(intro)}</p>`);
    }

    const items = (parsed.recommandations ?? []).filter(
        (item) => item.action?.trim() || item.detail?.trim(),
    );
    if (items.length > 0) {
        const listItems = items
            .map((item) => {
                const action = item.action?.trim();
                const detail = item.detail?.trim();
                const actionHtml = action ? `<strong>${toActionHtml(action)}</strong>` : '';
                const separator = action && detail ? ': ' : '';
                const detailHtml = detail ? toInlineHtml(detail) : '';

                return `<li>${actionHtml}${separator}${detailHtml}</li>`;
            })
            .join('');
        parts.push(`<ul>${listItems}</ul>`);
    }

    // Parsed, but nothing usable inside (empty fields): fall back to the readable text rather than nothing.
    return parts.length > 0 ? parts.join('') : toParagraphs(content.trim());
};
